import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  GitPullRequest,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Github
} from 'lucide-react';
import { apiClient, GitHubRepository, GitHubFile, CreatePullRequestRequest, GitLabRepository } from '../../api/client';
import { GitHubFileBrowser } from './GitHubFileBrowser';
import { GitLabFileBrowser } from './GitLabFileBrowser';
import { YAMLDiffPreview } from './YAMLDiffPreview';

// Unified repository type for both GitHub and GitLab
interface UnifiedRepository {
  id: string;
  name: string; // Display name (repo_full_name or project_path)
  defaultBranch: string;
  isPrivate: boolean;
  isFork: boolean;
  platform: 'github' | 'gitlab';
  // Keep original for API calls
  githubRepo?: GitHubRepository;
  gitlabRepo?: GitLabRepository;
}

interface GitHubPRCreationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  yamlContent: string;
  sourceType: string;
  sourceId?: string;
  defaultTitle?: string;
  defaultDescription?: string;
}

interface PRCreationState {
  step: 'repository' | 'file' | 'preview' | 'creating' | 'success';
  repositories: UnifiedRepository[];
  selectedRepository: UnifiedRepository | null;
  selectedFile: GitHubFile | null;
  targetFilePath: string;
  prTitle: string;
  prDescription: string;
  commitMessage: string;
  updateMode: 'append' | 'replace' | 'smart_merge';
  existingContent: string;
  mergedContent: string;
  createdPR: any | null;
  error: string | null;
  loading: boolean;
}

export const GitHubPRCreationDialog: React.FC<GitHubPRCreationDialogProps> = ({
  open,
  onOpenChange,
  yamlContent,
  sourceType,
  sourceId,
  defaultTitle = '',
  defaultDescription = '',
}) => {
  const [state, setState] = useState<PRCreationState>({
    step: 'repository',
    repositories: [],
    selectedRepository: null,
    selectedFile: null,
    targetFilePath: '',
    prTitle: defaultTitle || `[KestrelAI] Add ${sourceType}`,
    prDescription: defaultDescription || '',
    commitMessage: '',
    updateMode: 'smart_merge',
    existingContent: '',
    mergedContent: '',
    createdPR: null,
    error: null,
    loading: false,
  });

  // Load repositories when dialog opens
  useEffect(() => {
    if (open) {
      loadRepositories();
    } else {
      // Reset state when dialog closes
      setState(prev => ({
        ...prev,
        step: 'repository',
        selectedRepository: null,
        selectedFile: null,
        targetFilePath: '',
        error: null,
        createdPR: null,
      }));
    }
  }, [open]);

  // Auto-generate commit message from PR title
  useEffect(() => {
    if (state.prTitle && !state.commitMessage) {
      setState(prev => ({
        ...prev,
        commitMessage: state.prTitle.replace('[KestrelAI] ', ''),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.prTitle]);

  const loadRepositories = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const allRepos: UnifiedRepository[] = [];

      // Load GitHub repos
      try {
        const ghResponse = await apiClient.getGitHubRepositories();
        for (const repo of (ghResponse.repositories || [])) {
          if (repo.is_enabled) {
            allRepos.push({
              id: repo.id,
              name: repo.repo_full_name,
              defaultBranch: repo.default_branch,
              isPrivate: repo.is_private,
              isFork: repo.is_fork,
              platform: 'github',
              githubRepo: repo,
            });
          }
        }
      } catch (err) {
        console.log('GitHub repositories not available');
      }

      // Load GitLab repos
      try {
        const glResponse = await apiClient.getGitLabRepositories();
        for (const repo of (glResponse.repositories || [])) {
          if (repo.is_enabled) {
            allRepos.push({
              id: repo.id,
              name: repo.project_path,
              defaultBranch: repo.default_branch,
              isPrivate: repo.visibility === 'private',
              isFork: repo.is_fork,
              platform: 'gitlab',
              gitlabRepo: repo,
            });
          }
        }
      } catch (err) {
        console.log('GitLab repositories not available');
      }

      setState(prev => ({
        ...prev,
        repositories: allRepos,
        loading: false,
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.message || 'Failed to load repositories',
        loading: false,
      }));
    }
  };

  const handleRepositorySelect = (repository: UnifiedRepository) => {
    setState(prev => ({
      ...prev,
      selectedRepository: repository,
      step: 'file',
      error: null,
    }));
  };

  const handleFileSelect = (file: GitHubFile, fullPath: string) => {
    setState(prev => ({
      ...prev,
      selectedFile: file,
      targetFilePath: fullPath,
      step: 'preview',
    }));
  };


  const createPullRequest = async () => {
    if (!state.selectedRepository) return;

    setState(prev => ({ ...prev, step: 'creating', error: null }));

    try {
      if (state.selectedRepository.platform === 'gitlab') {
        // GitLab: Create merge request
        const response = await apiClient.createGitLabMergeRequest({
          repository_id: state.selectedRepository.id,
          target_file: state.targetFilePath,
          content: yamlContent,
          mr_title: state.prTitle,
          mr_description: state.prDescription,
          commit_message: state.commitMessage,
          update_mode: state.updateMode,
          source_type: sourceType,
          source_id: sourceId,
        });

        setState(prev => ({
          ...prev,
          step: 'success',
          createdPR: {
            number: response.merge_request?.iid || '',
            title: state.prTitle,
            url: response.merge_request?.url || '',
            branch: response.merge_request?.branch || '',
          },
          mergedContent: '',
        }));
      } else {
        // GitHub: Create pull request
        const request: CreatePullRequestRequest = {
          repository_id: state.selectedRepository.id,
          target_file: state.targetFilePath,
          yaml_content: yamlContent,
          pr_title: state.prTitle,
          pr_description: state.prDescription,
          commit_message: state.commitMessage,
          update_mode: state.updateMode,
          source_type: sourceType,
          source_id: sourceId,
        };

        const response = await apiClient.createGitHubPullRequest(request);

        setState(prev => ({
          ...prev,
          step: 'success',
          createdPR: response.pull_request,
          mergedContent: '',
        }));
      }
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        step: 'preview',
        error: error.response?.data?.message || `Failed to create ${state.selectedRepository?.platform === 'gitlab' ? 'merge request' : 'pull request'}`,
      }));
    }
  };

  const canProceedToPreview = () => {
    return state.selectedRepository && state.targetFilePath.trim();
  };

  const canCreatePR = () => {
    return state.prTitle.trim() && state.commitMessage.trim();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto sm:mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitPullRequest className="h-5 w-5" />
            Deploy to CI/CD
          </DialogTitle>
          <DialogDescription>
            Create a pull request to deploy your {sourceType} to CI/CD
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6 overflow-x-auto pb-2">
          <div className={`flex items-center gap-1 sm:gap-2 flex-shrink-0 ${state.step === 'repository' ? 'text-primary' : state.repositories.length > 0 ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${state.step === 'repository' ? 'bg-primary text-primary-foreground' : state.repositories.length > 0 ? 'bg-muted text-muted-foreground' : 'bg-muted text-muted-foreground'}`}>
              1
            </div>
            <span className="text-xs sm:text-sm whitespace-nowrap">Repository</span>
          </div>
          <div className="w-2 sm:w-4 h-px bg-border flex-shrink-0" />
          <div className={`flex items-center gap-1 sm:gap-2 flex-shrink-0 ${state.step === 'file' ? 'text-primary' : state.selectedRepository ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${state.step === 'file' ? 'bg-primary text-primary-foreground' : state.selectedRepository ? 'bg-muted text-muted-foreground' : 'bg-muted text-muted-foreground'}`}>
              2
            </div>
            <span className="text-xs sm:text-sm whitespace-nowrap">File</span>
          </div>
          <div className="w-2 sm:w-4 h-px bg-border flex-shrink-0" />
          <div className={`flex items-center gap-1 sm:gap-2 flex-shrink-0 ${state.step === 'preview' || state.step === 'creating' ? 'text-primary' : canProceedToPreview() ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${state.step === 'preview' || state.step === 'creating' ? 'bg-primary text-primary-foreground' : canProceedToPreview() ? 'bg-muted text-muted-foreground' : 'bg-muted text-muted-foreground'}`}>
              3
            </div>
            <span className="text-xs sm:text-sm whitespace-nowrap">Preview</span>
          </div>
        </div>

        {/* Error Display */}
        {state.error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        )}

        {/* Step Content */}
        <div className="min-h-[400px]">
          {/* Step 1: Repository Selection */}
          {state.step === 'repository' && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-medium">Select Repository</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose the repository where you want to create the pull request or merge request
                </p>
              </div>

              {state.loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span>Loading repositories...</span>
                </div>
              ) : state.repositories.length === 0 ? (
                <Alert>
                  <Github className="h-4 w-4" />
                  <AlertDescription>
                    No enabled repositories found. Please ensure your GitHub or GitLab integration is set up and repositories are enabled in CI/CD Integrations.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="grid gap-3">
                  {state.repositories.map((repo) => (
                    <div
                      key={`${repo.platform}-${repo.id}`}
                      className="p-4 border rounded-lg cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => handleRepositorySelect(repo)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{repo.name}</div>
                          <div className="text-sm text-muted-foreground mt-1">
                            Default branch: {repo.defaultBranch}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-xs ${repo.platform === 'gitlab' ? 'border-orange-300 text-orange-600' : 'border-gray-300 text-gray-600'}`}>
                            {repo.platform === 'gitlab' ? 'GitLab' : 'GitHub'}
                          </Badge>
                          {repo.isPrivate && (
                            <Badge variant="secondary" className="text-xs">Private</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: File Selection */}
          {state.step === 'file' && state.selectedRepository && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-medium">Select Target File</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {state.selectedRepository.platform === 'gitlab'
                    ? 'Enter the file path in the repository for your ' + sourceType
                    : 'Browse the repository and select a file for your ' + sourceType
                  }
                </p>
              </div>

              {state.selectedRepository.platform === 'gitlab' && state.selectedRepository.gitlabRepo ? (
                <GitLabFileBrowser
                  repository={state.selectedRepository.gitlabRepo}
                  onFileSelect={handleFileSelect}
                  onNewFileSelect={(directoryPath, fullPath) => {
                    setState(prev => ({
                      ...prev,
                      selectedFile: null,
                      targetFilePath: fullPath,
                      step: 'preview',
                      error: null,
                    }));
                  }}
                  selectedPath={state.targetFilePath}
                />
              ) : state.selectedRepository.githubRepo ? (
                <GitHubFileBrowser
                  repository={state.selectedRepository.githubRepo}
                  onFileSelect={handleFileSelect}
                  onNewFileSelect={(directoryPath, fullPath) => {
                    setState(prev => ({
                      ...prev,
                      selectedFile: null,
                      targetFilePath: fullPath,
                      step: 'preview',
                      error: null,
                    }));
                  }}
                  selectedPath={state.targetFilePath}
                />
              ) : null}
            </div>
          )}

          {/* Step 3: Preview & Configuration */}
          {(state.step === 'preview' || state.step === 'creating') && (
            <div className="space-y-6">
              <div>
                <Label className="text-base font-medium">
                  {state.selectedRepository?.platform === 'gitlab' ? 'Merge Request' : 'Pull Request'} Configuration
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure your {state.selectedRepository?.platform === 'gitlab' ? 'merge request' : 'pull request'} details and preview the changes
                </p>
              </div>

              <div className="grid gap-4">
                <div>
                  <Label htmlFor="prTitle">{state.selectedRepository?.platform === 'gitlab' ? 'Merge Request' : 'Pull Request'} Title *</Label>
                  <Input
                    id="prTitle"
                    value={state.prTitle}
                    onChange={(e) => setState(prev => ({ ...prev, prTitle: e.target.value }))}
                    placeholder="[KestrelAI] Add network policy for frontend"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="prDescription">Description</Label>
                  <Textarea
                    id="prDescription"
                    value={state.prDescription}
                    onChange={(e) => setState(prev => ({ ...prev, prDescription: e.target.value }))}
                    placeholder="Optional description for your pull request..."
                    className="mt-1 min-h-[80px]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="commitMessage">Commit Message *</Label>
                    <Input
                      id="commitMessage"
                      value={state.commitMessage}
                      onChange={(e) => setState(prev => ({ ...prev, commitMessage: e.target.value }))}
                      placeholder="Add network policy for frontend"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="updateMode">Update Mode</Label>
                    <Select
                      value={state.updateMode}
                      onValueChange={(value: any) => setState(prev => ({ ...prev, updateMode: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="smart_merge">Smart Merge (Recommended)</SelectItem>
                        <SelectItem value="append">Append</SelectItem>
                        <SelectItem value="replace">Replace</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* YAML Preview */}
              <div>
                <Label className="text-base font-medium">YAML Content Preview</Label>
                <YAMLDiffPreview
                  yamlContent={yamlContent}
                  updateMode={state.updateMode}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {state.step === 'success' && state.createdPR && (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-emerald-600 dark:text-emerald-400" />
              </div>

              <div>
                <h3 className="text-lg font-semibold">
                  {state.selectedRepository?.platform === 'gitlab' ? 'Merge Request' : 'Pull Request'} Created Successfully!
                </h3>
                <p className="text-muted-foreground mt-1">
                  Your {sourceType} has been deployed to CI/CD
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="font-medium">
                  {state.selectedRepository?.platform === 'gitlab' ? 'MR' : 'PR'} #{state.createdPR.number}: {state.createdPR.title}
                </div>
                <div className="text-sm text-muted-foreground">
                  Repository: {state.selectedRepository?.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  Branch: {state.createdPR.branch}
                </div>
              </div>

              <div className="flex gap-2 justify-center">
                <Button asChild>
                  <a href={state.createdPR.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View {state.selectedRepository?.platform === 'gitlab' ? 'Merge Request' : 'Pull Request'}
                  </a>
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {state.step !== 'success' && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>

            {state.step === 'repository' && (
              <Button
                onClick={() => setState(prev => ({ ...prev, step: 'file' }))}
                disabled={!state.selectedRepository}
              >
                Continue
              </Button>
            )}

            {state.step === 'file' && (
              <Button
                onClick={() => setState(prev => ({ ...prev, step: 'preview' }))}
                disabled={!canProceedToPreview()}
              >
                Preview Changes
              </Button>
            )}

            {state.step === 'preview' && (
              <Button
                onClick={createPullRequest}
                disabled={!canCreatePR()}
              >
                <GitPullRequest className="h-4 w-4 mr-2" />
                Create {state.selectedRepository?.platform === 'gitlab' ? 'Merge Request' : 'Pull Request'}
              </Button>
            )}

            {state.step === 'creating' && (
              <Button disabled>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};